import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ClipboardCheck, ChevronRight, RefreshCw, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { InterviewReport } from "./InterviewReport";

const GRADING_STEPS = ["Reviewing your answers", "Weighing communication and professionalism", "Assessing judgment, teamwork and integrity", "Writing your interview report"];

// Honest loading state: the grader is a single AI pass, so these lines describe what it evaluates — not live progress.
export function GradingState({ compact = false, onResults }) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % GRADING_STEPS.length), 3500); return () => clearInterval(t); }, []);
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-4" : "p-6 sm:p-8"}`} data-testid="grading-state">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0"><Loader2 className="w-5 h-5 animate-spin" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-xl text-slate-900">Your interview is finished. Your results are being prepared.</p>
          <p className="text-sm text-slate-600 mt-1">The panel is reviewing the whole transcript at once — your detailed report may take a minute or two.</p>
          <p className="mt-3 text-xs font-semibold text-slate-500 inline-flex items-center gap-2" data-testid="grading-step"><span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> {GRADING_STEPS[i]}…</p>
          <p className="mt-3 text-xs text-slate-500">You don't have to wait here — grading continues in the background and the report will be waiting under <b>Results</b>.</p>
          {onResults && <Button size="sm" variant="outline" className="mt-3" onClick={onResults} data-testid="grading-go-results">Go to Results</Button>}
        </div>
      </div>
    </div>
  );
}

const scoreTone = (n) => (n >= 85 ? "text-green-600" : n >= 70 ? "text-amber-600" : "text-red-600");

export function InterviewResults({ onNew, initialId = null }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null); // full result doc
  const load = () => api.get("/interview/history").then(({ data }) => setItems(data.interviews)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const grading = (items || []).some((x) => x.status === "grading");
  useEffect(() => { if (!grading) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [grading]);
  const view = async (id) => { try { const { data } = await api.get(`/interview/${id}/result`); setOpen(data); window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ } };
  useEffect(() => { if (initialId) view(initialId); }, [initialId]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = async (id) => { try { await api.post(`/interview/${id}/finish`); load(); } catch { /* ignore */ } };

  if (open?.scores && open.scores.overall != null) {
    return (
      <div>
        <button type="button" onClick={() => { setOpen(null); load(); }} className="text-sm font-semibold text-slate-600 hover:text-primary mb-2" data-testid="results-back">← All results</button>
        <InterviewReport scores={open.scores} messages={open.messages || []} onNew={onNew} />
      </div>
    );
  }
  if (items === null) return <p className="text-sm text-slate-500">Loading…</p>;
  const graded = items.filter((x) => x.status !== "in_progress" && x.status !== "demo_complete"); // old 2-question demos were never graded
  if (!graded.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="results-empty">
        <Trophy className="w-8 h-8 text-slate-300 mx-auto" />
        <p className="font-heading text-xl text-slate-900 mt-3">No graded boards yet</p>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">Complete a Mock Oral Board and your score and panel feedback will show up here.</p>
        <Button className="mt-4 bg-primary hover:bg-primary/90" onClick={onNew} data-testid="results-start">Start a board</Button>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="results-list">
      {graded.map((x) => {
        const d = new Date(x.created_at); const dept = x.department?.name;
        const s = x.scores; const failed = x.status === "completed" && s && s.overall == null;
        return (
          <div key={x.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-4" data-testid={`result-${x.id}`}>
            <div className="w-16 text-center shrink-0">
              {x.status === "grading" ? <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" data-testid={`result-grading-${x.id}`} />
                : x.status === "terminated" ? <span className="text-xs font-bold text-slate-400">ENDED</span>
                : failed ? <span className="text-xs font-bold text-red-600">—</span>
                : <span className={`font-heading text-3xl ${scoreTone(s?.overall || 0)}`} data-testid={`result-score-${x.id}`}>{s?.overall}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{dept ? `Department focus · ${dept}` : x.demo ? "General board · free" : "General board"}<span className="text-slate-400 font-normal"> · {d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">
                {x.status === "grading" ? "Your interview is complete — results are still being prepared (usually a minute or two)."
                  : x.status === "terminated" ? "Ended early by the panel — no report."
                  : failed ? "Grading was interrupted." : `${s?.pacing?.duration_label ? s.pacing.duration_label + " · " : ""}${x.response_mode === "text" ? "typed" : x.response_mode === "voice" ? "spoken" : "voice + text"}${s?.pacing?.assessment ? ` · pacing: ${s.pacing.assessment}` : ""}`}
              </p>
            </div>
            {x.status === "completed" && !failed && <Button size="sm" variant="outline" onClick={() => view(x.id)} data-testid={`result-open-${x.id}`}>Report <ChevronRight className="w-4 h-4 ml-1" /></Button>}
            {failed && <Button size="sm" variant="outline" onClick={() => retry(x.id)} data-testid={`result-retry-${x.id}`}><RefreshCw className="w-4 h-4 mr-1" /> Retry grading</Button>}
          </div>
        );
      })}
      <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> Scores are practice estimates to guide your preparation — not a prediction of any department's result. <button type="button" className="underline" onClick={() => navigate("/dashboard")}>Dashboard</button></p>
    </div>
  );
}
